import React, { useEffect } from "react";
import { MultiSelect, MultiSelectOption } from "@utahdts/utah-design-system";
import useFacilityFilterOptions from "../../hooks/siteVisits/useFacilityFilterOptions";
import SectionConditionIndicatorFilter from "./SectionConditionIndicatorFilter";

const FIELD_ERROR_MESSAGES = {
  facilityTypes: "Unable to load facility types.",
  statuses: "Unable to load facility statuses.",
  treatmentProcesses: "Unable to load treatment processes.",
  storageTankTypes: "Unable to load storage tank types.",
  facilityStatusReasons: "Unable to load status reasons.",
  systemIndicatorTypes: "Unable to load system indicators.",
  facilityIndicatorTypes: "Unable to load facility indicators.",
};

const SectionConditionFacilityFilter = ({
  facilityTypes,
  treatmentProcesses,
  storageTankTypes,
  facilityStatuses,
  facilityStatusReasons,
  systemIndicators,
  facilityIndicators,
  onFacilityTypesChange,
  onTreatmentProcessesChange,
  onStorageTankTypesChange,
  onFacilityStatusesChange,
  onFacilityStatusReasonsChange,
  onSystemIndicatorsChange,
  onFacilityIndicatorsChange,
  canEdit,
}) => {
  const {
    data: facilityFilterData,
    loading: facilityFilterLoading,
    errors: facilityFilterErrors,
    fetchTreatmentProcesses,
    fetchStorageTankTypes,
    fetchFacilityStatusReasons,
  } = useFacilityFilterOptions();

  const showTreatmentProcesses = facilityTypes.includes("TP");
  const showStorageTankTypes = facilityTypes.includes("ST");
  const showFacilityStatusReasons = facilityStatuses.includes("I");

  useEffect(() => {
    if (showTreatmentProcesses) {
      fetchTreatmentProcesses();
    } else if (treatmentProcesses.length > 0) {
      onTreatmentProcessesChange([]);
    }
  }, [showTreatmentProcesses, fetchTreatmentProcesses, onTreatmentProcessesChange, treatmentProcesses.length]);

  useEffect(() => {
    if (showStorageTankTypes) {
      fetchStorageTankTypes();
    } else if (storageTankTypes.length > 0) {
      onStorageTankTypesChange([]);
    }
  }, [showStorageTankTypes, fetchStorageTankTypes, onStorageTankTypesChange, storageTankTypes.length]);

  useEffect(() => {
    if (showFacilityStatusReasons) {
      fetchFacilityStatusReasons();
    } else if (facilityStatusReasons.length > 0) {
      onFacilityStatusReasonsChange([]);
    }
  }, [showFacilityStatusReasons, fetchFacilityStatusReasons, onFacilityStatusReasonsChange, facilityStatusReasons.length]);

  const anyLoading = Object.values(facilityFilterLoading).some(Boolean);
  const activeErrors = Object.entries(facilityFilterErrors)
    .filter(([, error]) => Boolean(error))
    .map(([field]) => FIELD_ERROR_MESSAGES[field] ?? "Unable to load one or more facility filter options.");

  return (
    <div className="site-visits-facility-filter">
      <strong>Facility Filter</strong>
      <div className="site-visits-option-fields-grid">
        <div>
          <MultiSelect
            id="facility-filter-types"
            isClearable
            isDisabled={!canEdit}
            label="Facility Type"
            values={facilityTypes}
            onChange={onFacilityTypesChange}
            className="site-visits-field-control"
            portalTarget={document.body}
          >
            {facilityFilterData.facilityTypes.map((facilityType) => (
              <MultiSelectOption
                key={facilityType.code}
                label={`${facilityType.code} - ${facilityType.description}`}
                searchValue={facilityType.code}
                value={facilityType.code}
              />
            ))}
          </MultiSelect>
        </div>
        <div>
          <MultiSelect
            id="facility-filter-statuses"
            isClearable
            isDisabled={!canEdit}
            label="Facility Status"
            values={facilityStatuses}
            onChange={onFacilityStatusesChange}
            className="site-visits-field-control"
            portalTarget={document.body}
          >
            {facilityFilterData.statuses.map((status) => (
              <MultiSelectOption
                key={status.code}
                label={status.description}
                value={status.code}
              />
            ))}
          </MultiSelect>
        </div>
        {showTreatmentProcesses && (
          <div className="site-visits-nested-field">
            <MultiSelect
              id="facility-filter-treatment-processes"
              isClearable
              isDisabled={!canEdit}
              label="Treatment Process"
              values={treatmentProcesses}
              onChange={onTreatmentProcessesChange}
              className="site-visits-field-control"
              portalTarget={document.body}
            >
              {facilityFilterData.treatmentProcesses.map((treatmentProcess) => (
                <MultiSelectOption
                  key={treatmentProcess.code}
                  label={`${treatmentProcess.code} - ${treatmentProcess.description}`}
                  searchValue={treatmentProcess.code}
                  value={treatmentProcess.code}
                />
              ))}
            </MultiSelect>
          </div>
        )}
        {showStorageTankTypes && (
          <div className="site-visits-nested-field">
            <MultiSelect
              id="facility-filter-storage-tank-types"
              isClearable
              isDisabled={!canEdit}
              label="Storage Tank Type"
              values={storageTankTypes}
              onChange={onStorageTankTypesChange}
              className="site-visits-field-control"
              portalTarget={document.body}
            >
              {facilityFilterData.storageTankTypes.map((storageTankType) => (
                <MultiSelectOption
                  key={storageTankType.code}
                  label={storageTankType.description}
                  value={storageTankType.code}
                />
              ))}
            </MultiSelect>
          </div>
        )}
        {showFacilityStatusReasons && (
          <div className="site-visits-nested-field">
            <MultiSelect
              id="facility-filter-status-reasons"
              isClearable
              isDisabled={!canEdit}
              label="Status Reasons to Exclude"
              values={facilityStatusReasons}
              onChange={onFacilityStatusReasonsChange}
              className="site-visits-field-control"
              portalTarget={document.body}
            >
              {facilityFilterData.facilityStatusReasons.map((statusReason) => (
                <MultiSelectOption
                  key={statusReason.code}
                  label={statusReason.description}
                  value={statusReason.code}
                />
              ))}
            </MultiSelect>
          </div>
        )}
      </div>
      <SectionConditionIndicatorFilter
        id="facility-filter-system-indicators"
        label="System Indicators"
        indicatorTypes={facilityFilterData.systemIndicatorTypes}
        indicators={systemIndicators}
        onIndicatorsChange={onSystemIndicatorsChange}
        canEdit={canEdit}
      />
      <SectionConditionIndicatorFilter
        id="facility-filter-facility-indicators"
        label="Facility Indicators"
        indicatorTypes={facilityFilterData.facilityIndicatorTypes}
        indicators={facilityIndicators}
        onIndicatorsChange={onFacilityIndicatorsChange}
        canEdit={canEdit}
      />
      {anyLoading && <p>Loading facility filter options...</p>}
      {activeErrors.map((message) => (
        <p key={message} className="site-visits-facility-filter-error">{message}</p>
      ))}
    </div>
  );
};

export default SectionConditionFacilityFilter;
